<template>
    <div
        class="nav-floating"
        :style="{ left: left + 'px' }"
        v-show="visible"
    >
        <div class="nav-floating-triangle-wrap">
            <div
                class="nav-floating-triangle"
                :style="{ top: triangleTop + 'px' }"
            ></div>
        </div>
        <div
            class="nav-floating-panel"
        >
            <header class="nav-floating-header">
                <div class="nav-floating-title-group">
                    <Icon :name="navItem?.icon?.name" size="md" color="var(--text-color-subtitle)" />
                    <h2 class="nav-floating-title">{{ navItem?.label }}</h2>
                </div>
                <button class="nav-floating-pin-btn" title="Open sidebar" @click="$emit('pin')">
                    <Icon name="fa-solid fa-thumbtack" size="sm" color="inherit" />
                </button>
            </header>
            <div class="nav-floating-content">
                <button class="nav-floating-nav-btn" @click="$emit('commit', navItem)">
                    <span class="nav-floating-nav-btn__label">{{ navItem?.label }}</span>
                </button>
                <slot name="floating-content"></slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@common/icon/Icon.vue';

defineProps({
    navItem: {
        type: Object,
        default: null,
    },
    triangleTop: {
        type: Number,
        default: 0,
    },
    visible: {
        type: Boolean,
        default: false,
    },
    left: {
        type: Number,
        default: 0,
    },
});

defineEmits(['pin', 'commit']);
</script>

<style scoped>
.nav-floating {
    position: absolute;
    top: 0;
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    z-index: var(--z-popover);
}

.nav-floating-triangle-wrap {
    position: relative;
    width: 8px;
    height: 100%;
    flex-shrink: 0;
}

.nav-floating-triangle {
    position: absolute;
    left: 0;
    width: 0;
    height: 0;
    pointer-events: none;
}

/* Outer triangle for boarder */
.nav-floating-triangle::before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-top: 9px solid transparent;
    border-bottom: 9px solid transparent;
    border-right: 9px solid var(--border-color-default);
}

/* Inner triangle for integrated look */
.nav-floating-triangle::after {
    content: '';
    position: absolute;
    top: 0;
    left: 1px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid var(--surface-default);
}

.nav-floating-panel {
    width: var(--nav-panel-width);
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--surface-default);
    border: 1px solid var(--border-color-default);
    box-shadow: var(--shadow-2);
    overflow: hidden;
}

.nav-floating-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-5) var(--space-6);
    border-bottom: 1px solid var(--border-color-default);
    flex-shrink: 0;
}

.nav-floating-title-group {
    display: inline-flex;
    align-items: center;
    gap: var(--space-4);
    overflow: hidden;
}

.nav-floating-title {
    margin: 0;
    font-size: var(--text-size-base);
    font-weight: var(--text-weight-medium);
    color: var(--text-color-title);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nav-floating-pin-btn {
    border: none;
    background: none;
    cursor: pointer;
    color: var(--text-color-subtitle);
    border-radius: var(--radius-full);
    width: var(--space-7);
    height: var(--space-7);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color var(--transition-default) ease, color var(--transition-default) ease;
}

.nav-floating-pin-btn:hover {
    background-color: var(--surface-subtle);
    color: var(--text-color-body);
}

.nav-floating-pin-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}

.nav-floating-content {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-5) var(--space-6) var(--space-6);
    min-height: 0;
}

.nav-floating-nav-btn {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-4) var(--space-5);
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-size-base);
    color: var(--text-color-body);
    border-radius: var(--radius-xs);
    transition: background-color var(--transition-default) ease;
    overflow: hidden;
}

.nav-floating-nav-btn__label {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
}

.nav-floating-nav-btn:hover {
    background-color: var(--surface-subtle);
}

.nav-floating-nav-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}
</style>
